import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { settingsAPI } from "./api";
import { useAuth } from "./AuthContext";
import {
  DEFAULT_MODEL,
  DEFAULT_FEATURE_FLAGS,
  AVAILABLE_MODELS,
  FEATURE_FLAG_PAGES,
} from "./constants/settingsConfig";

/**
 * SettingsContext — admin settings backed by the platform_settings API.
 *
 * On mount we fetch the singleton row and hydrate the context. Updates are
 * sent to the server and the response replaces local state, keeping clients
 * consistent with the server's shallow-merge semantics for feature_flags.
 *
 * `loading` is exposed so route guards / sidebars can avoid flicker or
 * incorrect redirects while the initial fetch is in flight.
 */

const SettingsContext = createContext(null);

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return ctx;
};

const sanitizeModel = (value) => {
  const isKnown = AVAILABLE_MODELS.some((m) => m.value === value);
  return isKnown ? value : DEFAULT_MODEL;
};

const sanitizeFlags = (flags) => {
  const out = { ...DEFAULT_FEATURE_FLAGS };
  if (flags && typeof flags === "object") {
    FEATURE_FLAG_PAGES.forEach((page) => {
      if (typeof flags[page.key] === "boolean") {
        out[page.key] = flags[page.key];
      }
    });
  }
  return out;
};

const normalizeServerPayload = (data) => ({
  selectedModel: sanitizeModel(data?.active_model),
  featureFlags: sanitizeFlags(data?.feature_flags),
});

const DEFAULT_STATE = {
  selectedModel: DEFAULT_MODEL,
  featureFlags: { ...DEFAULT_FEATURE_FLAGS },
};

export const SettingsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Track whether we've successfully hydrated at least once. Until then,
  // route guards should not act on feature flags (the user might lose access
  // to a page they're allowed to see just because the fetch hasn't completed).
  const hydratedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await settingsAPI.getSettings();
      setState(normalizeServerPayload(res.data));
      hydratedRef.current = true;
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 401) {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever the authenticated user changes: hydrate after login,
  // reset to defaults after logout. The endpoint requires authentication so
  // there's no point calling it while the user is null.
  useEffect(() => {
    if (authLoading) return;
    if (user) {
      refresh();
    } else {
      setState(DEFAULT_STATE);
      hydratedRef.current = false;
      setError(null);
      setLoading(false);
    }
  }, [user, authLoading, refresh]);

  const setSelectedModel = useCallback(async (value) => {
    const sanitized = sanitizeModel(value);
    // Optimistic update so the dropdown feels instant.
    setState((prev) => ({ ...prev, selectedModel: sanitized }));
    try {
      const res = await settingsAPI.setActiveModel(sanitized);
      setState(normalizeServerPayload(res.data));
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  const setFeatureFlag = useCallback(async (key, enabled) => {
    const next = !!enabled;
    // Optimistic update for snappy toggle response.
    setState((prev) => ({
      ...prev,
      featureFlags: { ...prev.featureFlags, [key]: next },
    }));
    try {
      const res = await settingsAPI.setFeatureFlag(key, next);
      setState(normalizeServerPayload(res.data));
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  const resetSettings = useCallback(async () => {
    const flagsReset = FEATURE_FLAG_PAGES.reduce((acc, page) => {
      acc[page.key] = true;
      return acc;
    }, {});
    setState({ selectedModel: DEFAULT_MODEL, featureFlags: flagsReset });
    try {
      const res = await settingsAPI.updateSettings({
        active_model: DEFAULT_MODEL,
        feature_flags: flagsReset,
      });
      setState(normalizeServerPayload(res.data));
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * isPageEnabled — visibility check used by sidebars and route guards.
   * Admins always see every page. Until the initial fetch completes we
   * default to "enabled" to avoid hiding pages from users who actually
   * have access.
   */
  const isPageEnabled = useCallback(
    (pageKey, { isAdmin = false } = {}) => {
      if (isAdmin) return true;
      if (!hydratedRef.current) return true;
      if (!(pageKey in state.featureFlags)) return true;
      return state.featureFlags[pageKey];
    },
    [state.featureFlags],
  );

  const value = {
    selectedModel: state.selectedModel,
    featureFlags: state.featureFlags,
    loading,
    error,
    refresh,
    setSelectedModel,
    setFeatureFlag,
    resetSettings,
    isPageEnabled,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
